/**
 * FB2 to Markdown Converter
 * Converts FB2 format to Markdown with images
 */

import type { FB2Book, FB2Section } from './fb2-parser';

export interface ConversionResult {
  markdown: string;
  images: Map<string, { contentType: string; data: string }>;
}

export class FB2ToMarkdownConverter {
  convert(fb2Book: FB2Book): ConversionResult {
    const lines: string[] = [];

    // Add title
    lines.push(`# ${fb2Book.metadata.title}`);
    lines.push('');

    // Add author
    if (fb2Book.metadata.author) {
      lines.push(`**Author:** ${fb2Book.metadata.author}`);
      lines.push('');
    }

    // Add genres
    if (fb2Book.metadata.genre && fb2Book.metadata.genre.length > 0) {
      lines.push(`**Genre:** ${fb2Book.metadata.genre.join(', ')}`);
      lines.push('');
    }

    // Add date
    if (fb2Book.metadata.date) {
      lines.push(`**Date:** ${fb2Book.metadata.date}`);
      lines.push('');
    }

    // Add cover image if exists
    if (fb2Book.metadata.coverImage && fb2Book.images.has(fb2Book.metadata.coverImage)) {
      const coverImage = fb2Book.images.get(fb2Book.metadata.coverImage);
      const ext = this.getExtensionFromContentType(coverImage?.contentType || 'image/jpeg');
      const coverFileName = fb2Book.metadata.coverImage.includes('.')
        ? fb2Book.metadata.coverImage
        : `${fb2Book.metadata.coverImage}.${ext}`;
      lines.push(`![Cover](assets/${coverFileName})`);
      lines.push('');
    }

    lines.push('---');
    lines.push('');

    // Convert sections
    for (const section of fb2Book.sections) {
      this.convertSection(section, lines, 2);
    }

    // Process images - replace references with asset paths
    const markdown = lines.join('\n');
    const processedMarkdown = this.processImageReferences(markdown, fb2Book.images);

    return {
      markdown: processedMarkdown,
      images: fb2Book.images
    };
  }

  private convertSection(section: FB2Section, lines: string[], level: number): void {
    // Add section title
    if (section.title) {
      const heading = '#'.repeat(level);
      lines.push(`${heading} ${section.title}`);
      lines.push('');
    }

    // Add content
    for (const content of section.content) {
      if (content) {
        lines.push(content);
        lines.push('');
      }
    }

    // Add subsections
    for (const subsection of section.subsections) {
      this.convertSection(subsection, lines, Math.min(level + 1, 6));
    }
  }

  private processImageReferences(markdown: string, images: Map<string, any>): string {
    // Replace FB2 image references (#image-id) with asset paths
    let processed = markdown;

    for (const [id, image] of images) {
      // Determine file extension from content type
      const ext = this.getExtensionFromContentType(image.contentType);
      const fileName = id.includes('.') ? id : `${id}.${ext}`;

      // Replace all occurrences of #image-id with assets/image-id.ext
      // Use word boundary to avoid partial matches
      processed = processed.replace(new RegExp(`#${id}(?!\\w)`, 'g'), `assets/${fileName}`);
    }

    return processed;
  }

  private getExtensionFromContentType(contentType: string): string {
    const map: Record<string, string> = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/bmp': 'bmp',
      'image/webp': 'webp',
      'image/svg+xml': 'svg'
    };

    return map[contentType.toLowerCase()] || 'jpg';
  }
}
